import { fetchCarbonSummary } from './api.ts';

export interface CarbonWarmSnapshot {
  siteId: string;
  summary: any;
  refreshedAt: number;
}

type CarbonWarmListener = (snapshot: CarbonWarmSnapshot) => void;

type CarbonWarmSiteState = {
  snapshot: CarbonWarmSnapshot | null;
  inFlight: boolean;
  timerId: number | null;
  listeners: Set<CarbonWarmListener>;
};

const carbonWarmStateBySite = new Map<string, CarbonWarmSiteState>();

function getSiteState(siteId: string): CarbonWarmSiteState {
  let state = carbonWarmStateBySite.get(siteId);
  if (!state) {
    state = {
      snapshot: null,
      inFlight: false,
      timerId: null,
      listeners: new Set(),
    };
    carbonWarmStateBySite.set(siteId, state);
  }
  return state;
}

function notifyListeners(siteId: string, snapshot: CarbonWarmSnapshot): void {
  const state = getSiteState(siteId);
  state.listeners.forEach((listener) => {
    try {
      listener(snapshot);
    } catch (error) {
      console.warn('[carbonWarmCache] listener failed:', error);
    }
  });
}

async function refreshCarbonSnapshot(siteId: string): Promise<CarbonWarmSnapshot> {
  const state = getSiteState(siteId);
  if (state.inFlight) {
    return state.snapshot || {
      siteId,
      summary: await fetchCarbonSummary(siteId),
      refreshedAt: Date.now(),
    };
  }

  state.inFlight = true;
  try {
    const summary = await fetchCarbonSummary(siteId);
    const snapshot: CarbonWarmSnapshot = {
      siteId,
      summary,
      refreshedAt: Date.now(),
    };
    state.snapshot = snapshot;
    notifyListeners(siteId, snapshot);
    return snapshot;
  } finally {
    state.inFlight = false;
  }
}

export function getCarbonWarmSnapshot(siteId: string): CarbonWarmSnapshot | null {
  return getSiteState(siteId).snapshot;
}

export async function primeCarbonWarmSnapshot(siteId: string): Promise<CarbonWarmSnapshot> {
  return refreshCarbonSnapshot(siteId);
}

export function subscribeCarbonWarmSnapshot(siteId: string, listener: CarbonWarmListener): () => void {
  const state = getSiteState(siteId);
  state.listeners.add(listener);

  if (state.snapshot) {
    listener(state.snapshot);
  }

  return () => {
    state.listeners.delete(listener);
  };
}

export function startCarbonWarmPolling(siteId: string, intervalMs = 5 * 60 * 1000): void {
  const state = getSiteState(siteId);
  if (state.timerId) return;

  const tick = () => {
    void refreshCarbonSnapshot(siteId).catch((error) => {
      console.warn('[carbonWarmCache] refresh failed:', error);
    });
  };

  tick();
  state.timerId = window.setInterval(tick, intervalMs);
}
